import { type ReactNode, type RefObject, useEffect, useState } from 'react';

import { Block, Select } from '../../components';
import {
  RECEIPTS_INFO,
  type ReceiptsTypeValue,
  receiptTypes,
} from '../../configs/receipts';
import { useLogEvent } from '../../hooks/analytics';
import styles from './receipt-config.module.scss';
import {
  B2bReceipt,
  CarrierMemberReceipt,
  CarrierMobileReceipt,
  DonateReceipt,
  PaymentCarrierReceipt,
} from './receipt-types';
import { type ReceiptFormControlRef, receiptStorage } from './utils';

import type { ReceiptTypeConfig } from './utils';

export const ReceiptConfig = ({
  availTypes = receiptTypes,
  defaultValue = receiptTypes[0],
  controlRef,
  config = {},
  validateBarcode,
  hint,
}: {
  availTypes: ReceiptsTypeValue[];
  defaultValue?: ReceiptsTypeValue;
  controlRef: RefObject<ReceiptFormControlRef>;
  config?: ReceiptTypeConfig;
  validateBarcode?: (str: string) => Promise<boolean>;
  hint?: ReactNode;
}) => {
  const logEvent = useLogEvent();
  const [type, setType] = useState(() => {
    const receiptInfo = receiptStorage.get();
    if (receiptInfo?.type) {
      return receiptInfo?.type;
    }

    return defaultValue;
  });

  const sortedAvailTypes = availTypes.toSorted((tA, tB) => {
    return RECEIPTS_INFO[tA].order - RECEIPTS_INFO[tB].order;
  });

  const options = sortedAvailTypes.map((t) => {
    return { value: t, text: RECEIPTS_INFO[t].name };
  });

  useEffect(() => {
    const receiptInfo = receiptStorage.get();
    receiptStorage.set({ ...receiptInfo, type });
  }, [type]);

  if (availTypes.length > 0 && !availTypes.includes(type)) {
    setType(availTypes[0]);
    return null;
  }

  const receiptComp = (() => {
    const emailFieldConfig = config?.[type]?.emailField ?? 'default';

    switch (type) {
      case 'carrier_mobile_barcode':
        return (
          <CarrierMobileReceipt
            controlRef={controlRef}
            validateBarcode={validateBarcode}
            emailFieldConfig={emailFieldConfig}
            storage={receiptStorage}
          />
        );
      case 'carrier_member':
        return (
          <CarrierMemberReceipt
            controlRef={controlRef}
            emailFieldConfig={emailFieldConfig}
            storage={receiptStorage}
          />
        );
      case 'b2b':
        return (
          <B2bReceipt
            controlRef={controlRef}
            emailFieldConfig={emailFieldConfig}
            storage={receiptStorage}
          />
        );
      case 'donate':
        return (
          <DonateReceipt
            controlRef={controlRef}
            emailFieldConfig={emailFieldConfig}
            storage={receiptStorage}
          />
        );
      case 'payment_carrier':
        return <PaymentCarrierReceipt controlRef={controlRef} />;

      default:
        throw new Error('type not support ');
    }
  })();

  return (
    <Block title="發票設定" className={styles['receipt-config']}>
      <div className="flex gap-3 flex-col">
        <Select
          onValueChange={(value) => {
            setType(value);
            logEvent('receiptOptionClick', { method: value });
          }}
          value={type}
          options={options}
        />
        {receiptComp}
        {hint}
      </div>
    </Block>
  );
};
